
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabase';

export interface ShortContentItem {
  id: string;
  title: string;
  content: string;
  content_type: 'tip' | 'fact' | 'highlight' | 'quote' | 'snippet';
  image_url: string | null;
  category: string | null;
  source: string | null;
  status: 'draft' | 'published' | 'archived' | 'scheduled';
  is_featured: boolean;
  publish_date: string | null;
  view_count: number;
  created_at: string;
  updated_at: string;
  author_id: string | null;
  display_order: number | null;
  slug: string | null;
}

export interface ShortContentInput {
  title: string;
  content: string;
  content_type: 'tip' | 'fact' | 'highlight' | 'quote' | 'snippet';
  image_url?: string | null;
  category?: string | null;
  source?: string | null;
  status: 'draft' | 'published' | 'archived' | 'scheduled';
  is_featured: boolean;
  publish_date?: string | Date | null;
  display_order?: number | null;
  slug?: string | null;
  author_id?: string | null;
}

type ValidContentType = 'tip' | 'fact' | 'highlight' | 'quote' | 'snippet';

interface UseShortContentOptions {
  limit?: number;
  page?: number;
  contentType?: ValidContentType | 'all' | undefined;
  category?: string;
  featured?: boolean;
  status?: 'draft' | 'published' | 'archived' | 'scheduled' | undefined;
}

interface UseShortContentResult {
  items: ShortContentItem[];
  isLoading: boolean;
  error: Error | null;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  fetchShortContent: (options?: UseShortContentOptions) => Promise<void>;
  createShortContent: (item: ShortContentInput) => Promise<{ data: ShortContentItem | null, error: Error | null }>;
  updateShortContent: (id: string, updates: Partial<ShortContentInput>) => Promise<{ data: ShortContentItem | null, error: Error | null }>;
  deleteShortContent: (id: string) => Promise<{ error: Error | null }>;
  incrementViewCount: (id: string) => Promise<void>;
  loadNextPage: () => Promise<void>;
  loadPrevPage: () => Promise<void>;
  refreshContent: () => Promise<void>;
}

// Enhanced cache implementation with TTL
const cache = new Map<string, { 
  data: ShortContentItem[], 
  totalCount: number, 
  timestamp: number,
  ttl: number 
}>();

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CACHE_FEATURED_DURATION = 10 * 60 * 1000; // 10 minutes for featured content

// Debounce utility
function useDebounce<T extends (...args: any[]) => any>(func: T, delay: number): T {
  const timeoutRef = useRef<NodeJS.Timeout>();
  
  return useCallback((...args: Parameters<T>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      func(...args);
    }, delay);
  }, [func, delay]) as T;
}

// Request deduplication
const activeRequests = new Map<string, Promise<any>>();

export function useShortContent(initialOptions: UseShortContentOptions = {}): UseShortContentResult {
  const [items, setItems] = useState<ShortContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialOptions.page ?? 1);
  
  // Use refs to prevent race conditions
  const abortControllerRef = useRef<AbortController | null>(null);
  const isInitializedRef = useRef(false);
  const lastFetchOptionsRef = useRef<string>('');

  // Memoize cache key generation
  const getCacheKey = useCallback((options: UseShortContentOptions) => {
    return JSON.stringify({
      limit: options.limit ?? 10,
      page: options.page ?? 1,
      contentType: options.contentType === 'all' ? undefined : options.contentType,
      category: options.category,
      featured: options.featured,
      status: options.status ?? 'published'
    });
  }, []);

  // Enhanced cache management
  const getCachedData = useCallback((cacheKey: string, featured?: boolean) => {
    const cached = cache.get(cacheKey);
    const ttl = featured ? CACHE_FEATURED_DURATION : CACHE_DURATION;
    
    if (cached && Date.now() - cached.timestamp < ttl) {
      console.log('Using cached data for key:', cacheKey);
      return cached;
    }
    
    // Clean up expired cache
    if (cached && Date.now() - cached.timestamp >= ttl) {
      cache.delete(cacheKey);
    }
    
    return null;
  }, []);

  const setCachedData = useCallback((cacheKey: string, data: ShortContentItem[], totalCount: number, featured?: boolean) => {
    const ttl = featured ? CACHE_FEATURED_DURATION : CACHE_DURATION;
    cache.set(cacheKey, {
      data: [...data],
      totalCount,
      timestamp: Date.now(),
      ttl
    });
  }, []);

  // Enhanced error handling
  const handleError = useCallback((err: any, context: string) => {
    console.error(`Error in ${context}:`, err);
    
    const errorMessage = err?.message || 'An unknown error occurred';
    const enhancedError = new Error(`${context}: ${errorMessage}`);
    
    setError(enhancedError);
    setIsLoading(false);
    
    return enhancedError;
  }, []);

  // Optimized fetch function with better query structure
  const fetchShortContent = useCallback(async (fetchOptions: UseShortContentOptions = {}) => {
    const options = { ...initialOptions, ...fetchOptions };
    const cacheKey = getCacheKey(options);
    
    // Prevent duplicate requests
    if (lastFetchOptionsRef.current === cacheKey && isLoading) {
      console.log('Skipping duplicate request for:', cacheKey);
      return;
    }

    // Check for active request
    if (activeRequests.has(cacheKey)) {
      console.log('Request already in progress for:', cacheKey);
      return activeRequests.get(cacheKey);
    }
    
    // Check cache first
    const cachedData = getCachedData(cacheKey, options.featured);
    if (cachedData) {
      setItems(cachedData.data);
      setTotalCount(cachedData.totalCount);
      setCurrentPage(options.page ?? 1);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    lastFetchOptionsRef.current = cacheKey;
    
    const limit = Math.min(options.limit ?? 10, 50); // Cap at 50 items max
    const page = Math.max(options.page ?? 1, 1); // Ensure page is at least 1
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    
    console.log(`Fetching short content:`, { 
      limit, page, from, to,
      contentType: options.contentType, 
      category: options.category, 
      featured: options.featured, 
      status: options.status ?? 'published'
    });
    
    setIsLoading(true);
    setError(null);
    
    const requestPromise = (async () => {
      try {
        // Build optimized query
        let query = supabase
          .from('short_content')
          .select(`
            id,
            title,
            content,
            content_type,
            image_url,
            category,
            source,
            status,
            is_featured,
            publish_date,
            view_count,
            created_at,
            updated_at,
            author_id,
            display_order,
            slug
          `, { count: 'exact' });
        
        // Apply filters efficiently
        if (options.status !== undefined) {
          query = query.eq('status', options.status);
        }
        
        if (options.contentType && options.contentType !== 'all') {
          query = query.eq('content_type', options.contentType);
        }
        
        if (options.category) {
          query = query.eq('category', options.category);
        }
        
        if (options.featured !== undefined) {
          query = query.eq('is_featured', options.featured);
        }
        
        // Optimized ordering - use index-friendly columns first
        query = query
          .order('display_order', { ascending: true, nullsFirst: false })
          .order('created_at', { ascending: false })
          .range(from, to);
        
        const { data, error: fetchError, count } = await query.abortSignal(abortController.signal);
        
        if (abortController.signal.aborted) {
          return;
        }
        
        if (fetchError) {
          throw new Error(`Database query failed: ${fetchError.message}`);
        }
        
        console.log(`Query successful - found ${data?.length || 0} items, total: ${count || 0}`);
        
        const resultData = data as ShortContentItem[] || [];
        const resultCount = count || 0;
        
        // Cache the results
        setCachedData(cacheKey, resultData, resultCount, options.featured);
        
        setItems(resultData);
        setTotalCount(resultCount);
        setCurrentPage(page);
        setIsLoading(false);
        setError(null);
        
      } catch (err: any) {
        if (!abortController.signal.aborted) {
          handleError(err, 'fetchShortContent');
        }
      } finally {
        activeRequests.delete(cacheKey);
      }
    })();
    
    activeRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }, [initialOptions, getCacheKey, getCachedData, setCachedData, handleError, isLoading]);

  // Debounced version for user interactions
  const debouncedFetch = useDebounce(fetchShortContent, 300);

  // Pagination helpers
  const limit = initialOptions.limit ?? 10;
  const totalPages = Math.ceil(totalCount / limit);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  const loadNextPage = useCallback(async () => {
    if (hasNextPage && !isLoading) {
      await fetchShortContent({ ...initialOptions, page: currentPage + 1 });
    }
  }, [hasNextPage, isLoading, currentPage, initialOptions, fetchShortContent]);

  const loadPrevPage = useCallback(async () => {
    if (hasPrevPage && !isLoading) {
      await fetchShortContent({ ...initialOptions, page: currentPage - 1 });
    }
  }, [hasPrevPage, isLoading, currentPage, initialOptions, fetchShortContent]);

  const refreshContent = useCallback(async () => {
    // Clear cache for current options
    const cacheKey = getCacheKey(initialOptions);
    cache.delete(cacheKey);
    
    // Force refresh
    lastFetchOptionsRef.current = '';
    await fetchShortContent(initialOptions);
  }, [initialOptions, getCacheKey, fetchShortContent]);

  // Enhanced CRUD operations with better error handling
  const createShortContent = useCallback(async (item: ShortContentInput) => {
    try {
      const formattedItem = {
        ...item,
        publish_date: item.publish_date && item.publish_date instanceof Date 
          ? item.publish_date.toISOString() 
          : item.publish_date as string | null
      };
      
      const { data, error: insertError } = await supabase
        .from('short_content')
        .insert(formattedItem)
        .select()
        .single();
        
      if (insertError) {
        throw new Error(`Failed to create content: ${insertError.message}`);
      }
      
      // Clear cache and refresh
      cache.clear();
      await refreshContent();
      
      return { data: data as ShortContentItem, error: null };
    } catch (err: any) {
      const error = handleError(err, 'createShortContent');
      return { data: null, error };
    }
  }, [refreshContent, handleError]);
  
  const updateShortContent = useCallback(async (id: string, updates: Partial<ShortContentInput>) => {
    try {
      const formattedUpdates = {
        ...updates,
        publish_date: updates.publish_date && updates.publish_date instanceof Date 
          ? updates.publish_date.toISOString() 
          : updates.publish_date as string | null
      };
      
      const { data, error: updateError } = await supabase
        .from('short_content')
        .update(formattedUpdates)
        .eq('id', id)
        .select()
        .single();
        
      if (updateError) {
        throw new Error(`Failed to update content: ${updateError.message}`);
      }
      
      // Update local state
      setItems(prev => prev.map(item => 
        item.id === id ? { ...item, ...updates } as ShortContentItem : item
      ));
      
      // Update cache
      cache.forEach((cachedItem, key) => {
        const updatedData = cachedItem.data.map(item => 
          item.id === id ? { ...item, ...updates } as ShortContentItem : item
        );
        cache.set(key, { ...cachedItem, data: updatedData });
      });
      
      return { data: data as ShortContentItem, error: null };
    } catch (err: any) {
      const error = handleError(err, 'updateShortContent');
      return { data: null, error };
    }
  }, [handleError]);
  
  const deleteShortContent = useCallback(async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('short_content')
        .delete()
        .eq('id', id);
        
      if (deleteError) {
        throw new Error(`Failed to delete content: ${deleteError.message}`);
      }
      
      // Update local state
      setItems(prev => prev.filter(item => item.id !== id));
      setTotalCount(prev => Math.max(0, prev - 1));
      
      // Update cache
      cache.forEach((cachedItem, key) => {
        const updatedData = cachedItem.data.filter(item => item.id !== id);
        cache.set(key, { 
          ...cachedItem, 
          data: updatedData, 
          totalCount: Math.max(0, cachedItem.totalCount - 1) 
        });
      });
      
      return { error: null };
    } catch (err: any) {
      const error = handleError(err, 'deleteShortContent');
      return { error };
    }
  }, [handleError]);
  
  const incrementViewCount = useCallback(async (id: string) => {
    try {
      await supabase.rpc('increment_short_content_view_count', { content_id: id });
      
      // Update local state
      setItems(prev => prev.map(item => 
        item.id === id ? { ...item, view_count: item.view_count + 1 } : item
      ));
      
      // Update cache
      cache.forEach((cachedItem, key) => {
        const updatedData = cachedItem.data.map(item => 
          item.id === id ? { ...item, view_count: item.view_count + 1 } : item
        );
        cache.set(key, { ...cachedItem, data: updatedData });
      });
    } catch (err) {
      console.error('Error incrementing view count:', err);
      // Don't set error state for view count failures
    }
  }, []);
  
  // Initial fetch - only run once
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      fetchShortContent(initialOptions);
    }
    
    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Clear active requests for this instance
      activeRequests.clear();
    };
  }, []); // Empty dependency array - only run once
  
  return {
    items,
    isLoading,
    error,
    totalCount,
    totalPages,
    currentPage,
    hasNextPage,
    hasPrevPage,
    fetchShortContent: debouncedFetch,
    createShortContent,
    updateShortContent,
    deleteShortContent,
    incrementViewCount,
    loadNextPage,
    loadPrevPage,
    refreshContent
  };
}
